/**
 * Merci : un trait par ligne sous la portion soulignée. Voir
 * `snippets/merci-trait.liquid` et le trait dans `styles/_baseline.scss`.
 *
 * Le trait est un `::after` absolu, posé sur `.merci-trait`. Un inline coupé
 * sur deux lignes donne à son enfant absolu une boîte qui va du début du
 * premier morceau à la fin du dernier : quand la fin est plus à gauche que le
 * début, la largeur est négative, et le trait disparaît. Relevé par Guillaume
 * le 2026-10-15 sur la phrase de marque en anglais ; mesuré aussi en français,
 * selon la largeur de l'écran.
 *
 * Le script découpe donc la portion en un `.merci-trait` par ligne, d'après la
 * position réelle des mots. Chaque morceau garde le trait du CSS tel quel — sa
 * hauteur, son chevauchement des jambages, sa couleur. Le texte coule comme
 * avant : des `span` sans marge ne déplacent aucune coupure.
 *
 * Pourquoi un script et non le CSS seul : `box-decoration-break` ne découpe
 * que le fond et le masque de l'élément lui-même. Masquer le `span` pour y
 * dessiner le trait rognait le bas des lettres, le trait chevauchant les
 * jambages ; un fond SVG, lui, aurait figé la couleur, qui suit le schéma de
 * chaque section.
 *
 * Le découpage se refait quand la largeur du bloc change, et une fois les
 * polices chargées. Sans script, la portion garde un seul trait, comme avant.
 */

/** Les nœuds d'origine de chaque portion, pour redécouper à neuf. */
const sources = new WeakMap();

/** La dernière largeur vue pour chaque bloc, pour ignorer les changements de hauteur. */
const largeurs = new WeakMap();

const observateur = new ResizeObserver((entrees) => {
  for (const { target, contentRect } of entrees) {
    if (largeurs.get(target) === contentRect.width) continue;
    largeurs.set(target, contentRect.width);
    target.querySelectorAll('.merci-trait-groupe').forEach(decouper);
  }
});

/**
 * Remplace chaque portion soulignée par un groupe, découpé ligne par ligne.
 * @param {ParentNode} racine
 */
function preparer(racine) {
  for (const trait of racine.querySelectorAll('.merci-trait:not([data-merci-trait-ligne])')) {
    const groupe = document.createElement('span');
    groupe.className = 'merci-trait-groupe';
    sources.set(groupe, [...trait.childNodes].map((noeud) => noeud.cloneNode(true)));
    trait.replaceWith(groupe);
    decouper(groupe);

    const bloc = groupe.parentElement;
    if (bloc) observateur.observe(bloc);
  }
}

/**
 * Pose chaque mot dans un `span` de mesure, puis regroupe ceux d'une même
 * ligne dans un `.merci-trait`. Les espaces et les `<br>` qui séparent deux
 * lignes restent hors des morceaux, pour ne pas étirer leur trait.
 * @param {HTMLElement} groupe
 */
function decouper(groupe) {
  const source = sources.get(groupe);
  if (!source) return;

  groupe.replaceChildren();
  for (const noeud of source) {
    if (noeud.nodeType !== Node.TEXT_NODE) {
      groupe.append(noeud.cloneNode(true));
      continue;
    }
    for (const morceau of (noeud.textContent ?? '').split(/(\s+)/)) {
      if (!morceau) continue;
      if (/^\s+$/.test(morceau)) {
        groupe.append(morceau);
        continue;
      }
      const mot = document.createElement('span');
      mot.textContent = morceau;
      groupe.append(mot);
    }
  }

  /** @type {Node[]} */
  const resultat = [];
  /** @type {Node[]} */
  let enAttente = [];
  /** @type {HTMLElement | null} */
  let ligne = null;
  let haut = NaN;

  for (const noeud of [...groupe.childNodes]) {
    if (!(noeud instanceof HTMLSpanElement)) {
      enAttente.push(noeud);
      continue;
    }

    const top = noeud.getBoundingClientRect().top;
    if (!ligne || Math.abs(top - haut) > 1) {
      resultat.push(...enAttente);
      ligne = document.createElement('span');
      ligne.className = 'merci-trait';
      ligne.dataset.merciTraitLigne = '';
      resultat.push(ligne);
      haut = top;
    } else {
      ligne.append(...enAttente);
    }
    enAttente = [];
    ligne.append(noeud.textContent ?? '');
  }
  resultat.push(...enAttente);

  groupe.replaceChildren(...resultat);
}

preparer(document);
document.fonts?.ready.then(() => document.querySelectorAll('.merci-trait-groupe').forEach(decouper));
document.addEventListener('shopify:section:load', (evenement) => {
  if (evenement.target instanceof Element) preparer(evenement.target);
});
